import { exerciseTraits } from "./exerciseTraits.js";
import type { Exercise } from "./schemas/exercise.js";
import type { LogSet } from "./schemas/logSet.js";
import type { LogSetKind } from "./schemas/logSet.js";
import type { Session } from "./schemas/session.js";
import type { NewWorkout, WorkoutSet } from "./schemas/workout.js";
import type { AthleteTargets, Program } from "./schemas/program.js";
import {
  computeSessionResult,
  type SessionResultExerciseLookup,
} from "./sessionResult.js";
import type { TimedSessionResult } from "./progressTypes.js";
import { resolveExerciseMusclesAbove } from "./volumeByMuscle.js";
import {
  computeWeeklyTargets,
  type WeeklyTargetRow,
} from "./weeklyTargets.js";
import { HARD_SET_MUSCLE_CONTRIB_MIN } from "./weeklyTargetActual.js";

// AP-3b — the in-workout contextual meter's data path. Folds the LIVE,
// still-unsaved logger session into the weekly target aggregate so the bar the
// athlete sees while training already counts the sets they've just logged
// (the current session is NOT in SessionResult yet — that's computed on save).
//
// Consolidation (CN, SCOUT AP-3b): every number here routes through the SAME
// machinery the saved path uses — `computeSessionResult` (which counts volume
// via dropsetSetTotals + hard-sets via the canonical RIR rule), `computeWeekly-
// Targets` (the Home meter's scorer), and `resolveExerciseMusclesAbove` (the
// one muscle-attribution resolver). Nothing re-derives hard-set/volume/muscle
// math a second time — a divergent copy would be the worst kind of duplication.
// The mobile caller only supplies the parsed draft (via buildPayload) + the
// saved sessions + the pinned program; all the folding lives here, pure.

// Exercise → SessionResultExerciseLookup: the same field projection the
// server's buildCatalogMap does inline, so the live meter feeds
// computeSessionResult the identical lookup the server writes with. `name` is
// the EN canonical (the snapshot the server stamps); the meter doesn't display
// it, but keeping the projection identical avoids two subtly-different maps.
export const exerciseToResultLookup = (
  ex: Exercise,
): SessionResultExerciseLookup => ({
  name: ex.name.en,
  primary: ex.primary,
  secondary: ex.secondary,
  muscleContrib: ex.muscleContrib,
  bodyweightLoadPct: ex.bodyweightLoadPct,
  isBodyweight: exerciseTraits(ex).isBodyweight,
});

// A permissive view of a WorkoutSet's optional payload fields. WorkoutSet is a
// discriminated union (per `type`); each variant carries a subset, but every
// field computeSessionResult reads is optional on LogSet, so we read them
// leniently rather than switching on all six variants.
type WorkoutSetPayload = {
  reps?: number;
  weight?: number;
  extra?: number;
  loadedSides?: 1 | 2;
  distanceM?: number;
  duration?: number;
  rir?: number;
  fail?: boolean;
  dropset?: LogSet["dropset"];
  p1rm?: number;
  rfl?: boolean;
  avgHrBpm?: number;
  calories?: number;
  completedAt?: string;
};

// One WorkoutSet → a LogSet-shaped object for computeSessionResult. `type`
// maps 1:1 to `kind` (same six enum strings); the payload fields carry over
// verbatim (buildPayload already did the locale-number parsing, dropset chain
// assembly, and effort canonicalisation — we never re-read raw draft fields).
// Synthetic id/userSub/performedAt: computeSessionResult reads none of them for
// the volume + hard-set aggregates the meter consumes (id is unused, userSub
// only echoes onto the result envelope, performedAt is the set clock which the
// windowing already covers via the session's startedAtMs).
const workoutSetToLogSet = (
  ws: WorkoutSet,
  exerciseSlug: string,
  nowIso: string,
  key: string,
): LogSet => {
  const p = ws as WorkoutSetPayload;
  return {
    id: key,
    userSub: "",
    exerciseSlug,
    kind: ws.type as LogSetKind,
    performedAt: nowIso,
    completedAt: p.completedAt ?? nowIso,
    createdAt: nowIso,
    updatedAt: nowIso,
    ...(p.reps !== undefined ? { reps: p.reps } : {}),
    ...(p.weight !== undefined ? { weight: p.weight } : {}),
    ...(p.extra !== undefined ? { extra: p.extra } : {}),
    ...(p.loadedSides !== undefined ? { loadedSides: p.loadedSides } : {}),
    ...(p.distanceM !== undefined ? { distanceM: p.distanceM } : {}),
    ...(p.duration !== undefined ? { duration: p.duration } : {}),
    ...(p.rir !== undefined ? { rir: p.rir } : {}),
    ...(p.fail !== undefined ? { fail: p.fail } : {}),
    ...(p.dropset !== undefined ? { dropset: p.dropset } : {}),
    ...(p.p1rm !== undefined ? { p1rm: p.p1rm } : {}),
    ...(p.rfl !== undefined ? { rfl: p.rfl } : {}),
    ...(p.avgHrBpm !== undefined ? { avgHrBpm: p.avgHrBpm } : {}),
    ...(p.calories !== undefined ? { calories: p.calories } : {}),
  };
};

// Flatten a parsed NewWorkout (entries[].sets[]) into the flat LogSet[] the
// server stores and computeSessionResult expects. Entry slug = entry.exerciseId.
const newWorkoutToLogSets = (
  workout: NewWorkout,
  nowIso: string,
): LogSet[] => {
  const out: LogSet[] = [];
  workout.entries.forEach((entry, ei) => {
    entry.sets.forEach((ws, si) => {
      out.push(workoutSetToLogSet(ws, entry.exerciseId, nowIso, `live-${ei}-${si}`));
    });
  });
  return out;
};

// The live draft → a provisional TimedSessionResult, folded into the weekly
// window at `nowMs`. priorHistory is empty on purpose: the meter needs volume +
// hard-sets, never PR flags. Returns null when the draft has no completed sets
// to fold (buildPayload already dropped the uncompleted ones upstream).
export const computeProvisionalSessionResult = (
  workout: NewWorkout,
  catalog: ReadonlyArray<Exercise>,
  profileBodyweightKg: number,
  nowMs: number,
): TimedSessionResult | null => {
  const nowIso = new Date(nowMs).toISOString();
  const sets = newWorkoutToLogSets(workout, nowIso);
  if (sets.length === 0) return null;
  const exerciseCatalog = new Map<string, SessionResultExerciseLookup>();
  for (const ex of catalog) exerciseCatalog.set(ex.slug, exerciseToResultLookup(ex));
  const session: Session = {
    id: "live",
    userSub: "",
    startedAt: nowIso,
    status: "saved",
    createdAt: nowIso,
    updatedAt: nowIso,
  };
  const result = computeSessionResult(
    {
      session,
      setsInSession: sets,
      exerciseCatalog,
      priorHistory: new Map(),
      profileBodyweightKg,
    },
    nowIso,
  );
  return { result, startedAtMs: nowMs };
};

// Keep only the target rows that belong to the exercise being trained now: a
// muscle row whose muscle the exercise SUBSTANTIALLY trains (contrib ≥0.5), or
// the exercise-axis row for this exact slug. This is a VISIBILITY threshold, not
// the scoring rule — hard-set scoring itself credits synergists fractionally
// below 0.5 (resolveExerciseMuscleCredits, AP-6); the strip just shows the
// primary movers ("chest 8/12, triceps 5/6" while pressing), not every synergist
// or the whole week's board.
export const filterTargetRowsForExercise = (
  rows: readonly WeeklyTargetRow[],
  exercise: Exercise,
): WeeklyTargetRow[] => {
  const muscles = new Set<string>(
    resolveExerciseMusclesAbove(exercise, HARD_SET_MUSCLE_CONTRIB_MIN),
  );
  return rows.filter((r) =>
    r.axis === "exercise" ? r.key === exercise.slug : muscles.has(r.key),
  );
};

// The one entrypoint the mobile hook calls: fold the live draft into the saved
// sessions, score against the pinned program, and narrow to the active
// exercise's rows. `draftWorkout` null (or no completed sets) → scores the
// saved sessions alone (the meter still reads while you set up the first set).
// Returns [] when there's nothing contextual to show (host hides the strip).
export const computeInWorkoutTargets = (
  savedSessions: readonly TimedSessionResult[],
  draftWorkout: NewWorkout | null,
  catalog: ReadonlyArray<Exercise>,
  program: Program | null,
  exercise: Exercise,
  profileBodyweightKg: number,
  nowMs: number,
  // AP-9 — the program's per-muscle weekly hard-set prescription, so a targeted
  // muscle the program never trains reads "no coverage" in the strip too. The
  // hook memoizes it on [program, catalog]; null degrades to no noCoverage flag.
  prescription: Record<string, number> | null = null,
  // PG-2 — the athlete's week-start day, so the live strip scores the same
  // calendar week as the Home meter. Omitted → Monday.
  weekStartDay?: number,
  // ME-3 — the athlete's own weekly targets (AthleteTargets). Passed so the live
  // strip scores the SAME targets as the Home meter; without it the strip scores
  // no target (the program's embedded targets are no longer read).
  athleteTargets: AthleteTargets | null = null,
): WeeklyTargetRow[] => {
  const provisional = draftWorkout
    ? computeProvisionalSessionResult(draftWorkout, catalog, profileBodyweightKg, nowMs)
    : null;
  const sessions = provisional
    ? [provisional, ...savedSessions]
    : [...savedSessions];
  const data = computeWeeklyTargets(
    sessions,
    catalog,
    program,
    nowMs,
    prescription,
    { weekStartDay },
    athleteTargets,
  );
  if (data === null) return [];
  return filterTargetRowsForExercise(data.rows, exercise);
};
